import { useEffect } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, generatePath } from 'react-router'

import { Box, Link, Text, VStack } from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useAnnouncementReadState } from '~/hooks/announcement/useAnnouncementReadState'
import { useListAnnouncements } from '~/hooks/announcement/useListAnnouncements'
import { latestPublishedAt } from '~/models/announcementUnread'
import { formatJstDateDisplay } from '~/models/dateTime'
import { routePaths } from '~/routes/routePaths'

/**
 * お知らせ一覧 (design `updates.html` の「システムアップデート」タブ相当)。
 *
 * design は各告知の本文を一覧に全文展開するが、本番原稿は 1 件で数十項目の長文に
 * なるため、一覧はタイトルと公開日だけにして本文は詳細画面に置く (Design Note D-UI-1)。
 * 並び順は backend が published_at 降順で返す順をそのまま使う。
 */
export const AnnouncementsPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading } = useListAnnouncements()
  const announcements = data?.announcements ?? []
  const { markRead } = useAnnouncementReadState()

  // 既読化は一覧が取得できた時だけ (取得失敗や読み込み中に mount しただけで
  // 既読にすると、見ていない告知のドットが消える)。詳細 page では既読化しない
  // (一覧にある他の新しい告知を見落とすため。Design Note D-4)。
  const latest = latestPublishedAt(announcements)
  useEffect(() => {
    if (latest !== undefined) markRead(latest)
  }, [latest, markRead])

  return (
    <PageLayout
      breadcrumbItems={[{ label: t('common:route.announcements'), bold: true }]}
    >
      <PageBody>
        <PageIntro description={t('page:announcements.description')} />
        <PageScrollArea>
          <RegionLoading isLoading={isLoading}>
            {isLoading ? null : announcements.length === 0 ? (
              <Box
                padding={6}
                color="Text.midEmphasis"
                textAlign="center"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="Border.lowEmphasis"
                borderRadius="lg"
              >
                {t('page:announcements.empty')}
              </Box>
            ) : (
              <VStack as="ul" alignItems="stretch" gap={4} listStyleType="none">
                {announcements.map((announcement) => (
                  <Box
                    key={announcement.announcementId}
                    as="li"
                    padding={4}
                    borderWidth="1px"
                    borderColor="Border.lowEmphasis"
                    borderRadius="lg"
                    backgroundColor="Surface.primary"
                  >
                    {announcement.publishedAt !== undefined && (
                      <Text
                        textStyle="captionNormal"
                        marginBottom={2}
                        color="Text.midEmphasis"
                      >
                        {formatJstDateDisplay(
                          timestampDate(announcement.publishedAt),
                        )}
                      </Text>
                    )}
                    <Link textStyle="label" asChild>
                      <RouterLink
                        to={generatePath(routePaths.announcementDetail.path, {
                          announcementId: announcement.announcementId,
                        })}
                      >
                        {announcement.title}
                      </RouterLink>
                    </Link>
                  </Box>
                ))}
              </VStack>
            )}
          </RegionLoading>
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
